import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from '../../common/withRouter';
import { Link } from 'react-router';
import { MotifTabControl, MotifTabNavigation } from '@ey-xd/motif-react';
import ViewReportHeader from '../../components/analytics/ViewReportHeader';
import * as authActions from '../../actions/authActions';
import * as analyticsReportActions from '../../actions/analyticsReportActions';
import * as reitActions from '../../actions/reitActions';
import * as periodActions from '../../actions/periodActions';
/**
 * ViewReportContainer component for Analytics Reports - View Report tab
 * @extends {React.Component}
 */
export class ViewReportContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedReportId: '',
      resetCheckboxes: false,
      selectedReport: '',
      reportOptions: [], // populate with available analytics reports when data is ready
    };

    this.isAdminUser = this.isAdminUser.bind(this);
    this.handleReportChange = this.handleReportChange.bind(this);
    this.handleRunReport = this.handleRunReport.bind(this);
  }

  componentDidMount() {
    if (this.props.clientId) {
      this.props.actions.loadAnalyticsReportDropdown(this.props.clientId);
      this.props.reitActions.fetchReits(this.props.clientId, true);
      this.props.periodActions.fetchReportPeriodsByClientId(
        this.props.clientId
      );
    }

    // load report data here
    // Example: set sample options until real data is wired
    // this.setState({ reportOptions: [{ value: 'analytics1', text: 'Analytics Report 1' }] });
  }

  componentDidUpdate(prevProps) {
    // Reload dropdown if clientId changes
    if (prevProps.clientId !== this.props.clientId && this.props.clientId) {
      this.props.actions.loadAnalyticsReportDropdown(this.props.clientId);
      this.props.reitActions.fetchReits(this.props.clientId, true);
      this.props.periodActions.fetchReportPeriodsByClientId(
        this.props.clientId
      );
    }
  }

  /**
   * Determines if the current user is a System Administrator or Engagement Administrator
   * @returns {boolean} True if user is an admin, otherwise false
   */
  isAdminUser() {
    const { currentUserAuthorizations, clientId } = this.props;

    if (!Array.isArray(currentUserAuthorizations)) {
      return false;
    }

    const isSysAdmin = authActions.isSystemAdministrator(
      currentUserAuthorizations
    );
    const isEngAdmin = authActions.isEngagementAdministrator(
      currentUserAuthorizations,
      clientId
    );

    return isSysAdmin || isEngAdmin;
  }

  /**
   * Handle analytics report selection change
   * @param {Object} event The change event
   */
  handleReportChange(eventOrValue) {
    // MotifSelect may provide value directly or via event.target.value
    let selectedId = '';
    if (eventOrValue && typeof eventOrValue === 'object') {
      selectedId =
        eventOrValue?.target?.value ?? eventOrValue?.detail?.value ?? '';
    } else {
      selectedId = eventOrValue ?? '';
    }

    // Normalize to string for consistent comparison with option values
    const selectedIdStr = String(selectedId);

    const selectedReport =
      this.props.analyticsReports.find(
        (report) => String(report.analyticsReportGUID) === selectedIdStr
      ) || null;

    this.setState({
      selectedReportId: selectedIdStr,
      selectedReport: selectedReport,
    });
  }

  handleRunReport() {
    // TODO: wire actual report execution
    // For now, no-op or add toast/loader integration
  }

  render() {
    const isAdmin = this.isAdminUser();
    const { selectedReportId } = this.state;

    const tabs = [
      <Link
        key="viewReport"
        to={`/client/${this.props.clientId}/analytics/viewReport`}
        tabIndex="-1"
      >
        <MotifTabControl
          className="motif-active"
          role="tab"
          aria-selected={true}
          data-testid="tbViewReport"
        >
          View Report
        </MotifTabControl>
      </Link>,
    ];

    if (isAdmin) {
      tabs.push(
        <Link
          key="managePublishedData"
          to={`/client/${this.props.clientId}/analytics/managePublishedData`}
          tabIndex="-1"
        >
          <MotifTabControl
            role="tab"
            aria-selected={false}
            data-testid="tbManagePublishedData"
          >
            Manage Published Data
          </MotifTabControl>
        </Link>
      );
    }

    return (
      <React.Fragment>
        <h2 data-testid="pghdrAnalytics">Analytics Reports</h2>

        <MotifTabNavigation variant="default">{tabs}</MotifTabNavigation>

        <ViewReportHeader
          selectedReport={this.state.selectedReport}
          onReportChange={this.handleReportChange}
          selectedReportId={selectedReportId}
          analyticsReports={this.props.analyticsReports}
          actions={this.props.actions}
          onRunReport={this.handleRunReport}
          resetCheckboxes={this.state.resetCheckboxes}
          reits={this.props.reits}
          reportPeriods={this.props.periods}
          checkedReits={[]}
          checkedPeriods={[]}
        />

        <div className="analytics-report-placeholder">
          <p>
            <b>
              Select Analytics Report → Select REIT → Select Report Period → Run
              Report.
            </b>
          </p>
        </div>
      </React.Fragment>
    );
  }
}

ViewReportContainer.propTypes = {
  currentUserAuthorizations: PropTypes.array,
  clientId: PropTypes.number,
  router: PropTypes.object,
  analyticsReports: PropTypes.array,
  actions: PropTypes.object.isRequired,
  reits: PropTypes.array,
  periods: PropTypes.array,
  reitActions: PropTypes.object,
  periodActions: PropTypes.object,
};

function mapStateToProps(state, ownProps) {
  let cid = Number.parseInt(ownProps.router.params.clientId);
  if (isNaN(cid)) {
    cid = 0;
  }

  return {
    currentUserAuthorizations: state.currentUserAuthorizations,
    clientId: cid,
    analyticsReports: state.analyticsReports || [],
    reits: state.reits,
    periods: state.periods,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(analyticsReportActions, dispatch),
    reitActions: bindActionCreators(reitActions, dispatch),
    periodActions: bindActionCreators(periodActions, dispatch),
  };
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ViewReportContainer)
);
